import React, { useEffect, useMemo, useState } from 'react'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import { CustomContentModal } from '../../../../components/CustomContentModal'
import Button from '../../../../components/Form/Button'
import Input from '../../../../components/Form/Input'
import Select from '../../../../components/Form/Select'
import { getInputStyles } from '../../../../components/Form/styles'
import {
  getMeterServiceTariffOptionsApi,
  MeterServiceTariffOptions,
  MeterServiceUpdatePayload,
  updateMeterServiceApi,
} from '../../../../apis/apis'
import { ServiceAgreement } from '../../../../apis/types'
import { pushToast } from '../../../../components/Toaster/Toaster.slice'
import { useAppDispatch } from '../../../../shared/redux/hooks'
import {
  toIsoDateValue,
  groupCcaTariffsByUtility,
  ccaTariffOptionKey,
  ccaTariffOptionLabel,
} from './serviceAgreementTariffUtils'

export interface ServiceAgreementEditModalProps {
  isOpen: boolean
  record: ServiceAgreement | null
  ccaProgram?: string
  isSubmitting?: boolean
  onClose: () => void
  onSaved: () => void | Promise<void>
}

type FormValues = {
  serviceAgreementId: string
  tariffSchedule: string
  tariffEffectiveDate: string
  tariffEndDate: string
  ccaTariffSchedule: string
  ccaTariffUtilityShortName: string
  ccaTariffEffectiveDate: string
  ccaTariffEndDate: string
  ccaVintageYear: string
  meterAccountType: 'BENEFITTING' | 'GENERATOR'
  postBillingTransition: boolean
}

const emptyValues: FormValues = {
  serviceAgreementId: '',
  tariffSchedule: '',
  tariffEffectiveDate: '',
  tariffEndDate: '',
  ccaTariffSchedule: '',
  ccaTariffUtilityShortName: '',
  ccaTariffEffectiveDate: '',
  ccaTariffEndDate: '',
  ccaVintageYear: '',
  meterAccountType: 'BENEFITTING',
  postBillingTransition: false,
}

function recordToValues(record: ServiceAgreement): FormValues {
  return {
    serviceAgreementId: record.serviceAgreementId ?? '',
    tariffSchedule: record.tariff?.schedule ?? '',
    tariffEffectiveDate: toIsoDateValue(record.tariffEffectiveDate) ?? '',
    tariffEndDate: toIsoDateValue(record.tariffEndDate) ?? '',
    ccaTariffSchedule: record.ccaTariff?.schedule ?? '',
    ccaTariffUtilityShortName: record.ccaTariff?.utilityShortName ?? '',
    ccaTariffEffectiveDate: toIsoDateValue(record.ccaTariffEffectiveDate) ?? '',
    ccaTariffEndDate: toIsoDateValue(record.ccaTariffEndDate) ?? '',
    ccaVintageYear: record.ccaVintageYear ?? '',
    meterAccountType: record.meterAccountType === 'GENERATOR' ? 'GENERATOR' : 'BENEFITTING',
    postBillingTransition: record.postBillingTransition === true,
  }
}

function displayLabel(value: string | boolean | null | undefined): string {
  if (value === true) return 'Yes'
  if (value === false) return 'No'
  if (value == null || value === '') return '—'
  if (value === 'BENEFITTING') return 'Benefitting'
  if (value === 'GENERATOR') return 'Generator'
  return String(value)
}

const ServiceAgreementEditModal: React.FC<ServiceAgreementEditModalProps> = ({
  isOpen,
  record,
  ccaProgram,
  onClose,
  onSaved,
}) => {
  const dispatch = useAppDispatch()
  const [step, setStep] = useState<'form' | 'review'>('form')
  const [draft, setDraft] = useState<FormValues | null>(null)
  const [options, setOptions] = useState<MeterServiceTariffOptions | null>(null)
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const initialValues = useMemo(
    () => (record != null ? recordToValues(record) : emptyValues),
    [record]
  )

  useEffect(() => {
    if (!isOpen || record == null) {
      setStep('form')
      setDraft(null)
      setOptions(null)
      return
    }

    let cancelled = false
    setLoadingOptions(true)
    void getMeterServiceTariffOptionsApi(record.id)
      .then((opts) => {
        if (!cancelled) setOptions(opts)
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setOptions(null)
          void dispatch(
            pushToast({
              type: 'error',
              message: 'Could not load tariff options',
              description: error instanceof Error ? error.message : undefined,
            })
          )
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingOptions(false)
      })

    return () => {
      cancelled = true
    }
  }, [isOpen, record, dispatch])

  if (!isOpen || record == null) return null

  const ccaApplies =
    options?.ccaApplies === true ||
    ccaProgram === 'ENROLLED' ||
    record.ccaTariff != null ||
    (options?.ccaTariffs?.length ?? 0) > 0

  const iouSchedule = draft?.tariffSchedule || initialValues.tariffSchedule
  const ccaGroups = groupCcaTariffsByUtility(
    options?.ccaTariffs ?? [],
    options?.ccaUtilityShortName ?? initialValues.ccaTariffUtilityShortName ?? null,
    iouSchedule
  )

  const serviceTariffOptions: Record<string, string> = Object.fromEntries(
    (options?.serviceTariffs ?? []).map((t) => [
      t.schedule,
      t.name ? `${t.schedule} — ${t.name}` : t.schedule,
    ])
  )
  if (initialValues.tariffSchedule && serviceTariffOptions[initialValues.tariffSchedule] == null) {
    serviceTariffOptions[initialValues.tariffSchedule] = initialValues.tariffSchedule
  }

  const baseline = initialValues
  const compare = draft ?? baseline
  const diffs: Array<{ label: string; from: string; to: string }> = []
  const pushDiff = (label: string, from: string | boolean, to: string | boolean) => {
    const a = displayLabel(from)
    const b = displayLabel(to)
    if (a !== b) diffs.push({ label, from: a, to: b })
  }
  pushDiff('Service agreement ID', baseline.serviceAgreementId, compare.serviceAgreementId)
  pushDiff('Service tariff', baseline.tariffSchedule, compare.tariffSchedule)
  pushDiff('Tariff effective start', baseline.tariffEffectiveDate, compare.tariffEffectiveDate)
  pushDiff('Tariff effective end', baseline.tariffEndDate, compare.tariffEndDate)
  if (ccaApplies) {
    pushDiff(
      'CCA tariff',
      baseline.ccaTariffSchedule
        ? baseline.ccaTariffUtilityShortName
          ? `${baseline.ccaTariffUtilityShortName} · ${baseline.ccaTariffSchedule}`
          : baseline.ccaTariffSchedule
        : '',
      compare.ccaTariffSchedule
        ? compare.ccaTariffUtilityShortName
          ? `${compare.ccaTariffUtilityShortName} · ${compare.ccaTariffSchedule}`
          : compare.ccaTariffSchedule
        : ''
    )
    pushDiff('CCA effective start', baseline.ccaTariffEffectiveDate, compare.ccaTariffEffectiveDate)
    pushDiff('CCA effective end', baseline.ccaTariffEndDate, compare.ccaTariffEndDate)
    pushDiff('CCA vintage year', baseline.ccaVintageYear, compare.ccaVintageYear)
  }
  pushDiff('Meter account type', baseline.meterAccountType, compare.meterAccountType)
  pushDiff('Post-billing transition', baseline.postBillingTransition, compare.postBillingTransition)

  const optiStatus = record.optiRateDetails?.status
  const willSyncOptiRate =
    (optiStatus === 'PENDING' || optiStatus === 'ENROLLED') &&
    compare.tariffSchedule !== baseline.tariffSchedule

  const handleConfirm = async () => {
    if (draft == null) return
    setIsSubmitting(true)
    try {
      const payload: MeterServiceUpdatePayload = {}
      if (draft.serviceAgreementId !== baseline.serviceAgreementId) {
        payload.serviceAgreementId = draft.serviceAgreementId
      }
      if (draft.tariffSchedule !== baseline.tariffSchedule) {
        payload.tariffSchedule = draft.tariffSchedule
      }
      if (draft.tariffEffectiveDate !== baseline.tariffEffectiveDate) {
        payload.tariffEffectiveDate = draft.tariffEffectiveDate || null
      }
      if (draft.tariffEndDate !== baseline.tariffEndDate) {
        payload.tariffEndDate = draft.tariffEndDate || null
      }
      if (ccaApplies) {
        if (
          draft.ccaTariffSchedule !== baseline.ccaTariffSchedule ||
          draft.ccaTariffUtilityShortName !== baseline.ccaTariffUtilityShortName
        ) {
          payload.ccaTariffSchedule = draft.ccaTariffSchedule || null
          if (draft.ccaTariffSchedule && draft.ccaTariffUtilityShortName) {
            payload.ccaTariffUtilityShortName = draft.ccaTariffUtilityShortName
          }
        }
        if (draft.ccaTariffEffectiveDate !== baseline.ccaTariffEffectiveDate) {
          payload.ccaTariffEffectiveDate = draft.ccaTariffEffectiveDate || null
        }
        if (draft.ccaTariffEndDate !== baseline.ccaTariffEndDate) {
          payload.ccaTariffEndDate = draft.ccaTariffEndDate || null
        }
        if (draft.ccaVintageYear !== baseline.ccaVintageYear) {
          payload.ccaVintageYear = draft.ccaVintageYear || null
        }
      }
      if (draft.meterAccountType !== baseline.meterAccountType) {
        payload.meterAccountType = draft.meterAccountType
      }
      if (draft.postBillingTransition !== baseline.postBillingTransition) {
        payload.postBillingTransition = draft.postBillingTransition
      }

      if (Object.keys(payload).length === 0) {
        onClose()
        return
      }

      await updateMeterServiceApi(record.id, payload)
      await onSaved()
      void dispatch(pushToast({ type: 'success', message: 'Service agreement updated' }))
      onClose()
    } catch (error) {
      void dispatch(
        pushToast({
          type: 'error',
          message: 'Could not update service agreement',
          description: error instanceof Error ? error.message : undefined,
        })
      )
      setStep('form')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <CustomContentModal isOpen={isOpen} onClose={isSubmitting ? () => undefined : onClose}>
      <div className="w-full max-w-2xl rounded-lg bg-white p-6 text-left shadow-xl dark:bg-slate-800">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          {step === 'form' ? 'Edit service agreement' : 'Confirm changes'}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Changes affect future billing and SABER.
        </p>

        {step === 'form' ? (
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={Yup.object({
              serviceAgreementId: Yup.string().required('Required'),
              tariffSchedule: Yup.string().required('Required'),
              meterAccountType: Yup.string().oneOf(['BENEFITTING', 'GENERATOR']).required(),
            })}
            onSubmit={(values) => {
              setDraft(values)
              setStep('review')
            }}
          >
            {({ values, setFieldValue, isSubmitting: formSubmitting }) => (
              <Form className="mt-4 space-y-3" aria-busy={loadingOptions}>
                <Field
                  as={Input}
                  name="serviceAgreementId"
                  label="Service agreement ID"
                  required
                />
                <Field
                  component={Select}
                  name="tariffSchedule"
                  label="Service tariff"
                  required
                  disabled={loadingOptions}
                  options={serviceTariffOptions}
                />
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field as={Input} type="date" name="tariffEffectiveDate" label="Tariff effective start" />
                  <Field as={Input} type="date" name="tariffEndDate" label="Tariff effective end" />
                </div>

                {ccaApplies ? (
                  <div className="space-y-3 rounded-md border border-slate-200 p-3 dark:border-slate-600">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      CCA
                    </p>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-200">
                        CCA tariff
                      </label>
                      <select
                        className={getInputStyles({ disabled: loadingOptions })}
                        disabled={loadingOptions}
                        value={
                          values.ccaTariffSchedule
                            ? values.ccaTariffUtilityShortName
                              ? `${values.ccaTariffSchedule}||${values.ccaTariffUtilityShortName}`
                              : values.ccaTariffSchedule
                            : ''
                        }
                        onChange={(e) => {
                          const raw = e.target.value
                          if (!raw) {
                            void setFieldValue('ccaTariffSchedule', '')
                            void setFieldValue('ccaTariffUtilityShortName', '')
                            return
                          }
                          const [schedule, utility] = raw.split('||')
                          void setFieldValue('ccaTariffSchedule', schedule)
                          void setFieldValue('ccaTariffUtilityShortName', utility ?? '')
                        }}
                      >
                        <option value="">Not set</option>
                        {ccaGroups.map((group) => (
                          <optgroup key={group.label} label={group.label}>
                            {group.tariffs.map((t) => {
                              const key = ccaTariffOptionKey(t)
                              return (
                                <option key={key || 'none'} value={key}>
                                  {ccaTariffOptionLabel(t)}
                                </option>
                              )
                            })}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Field
                        as={Input}
                        type="date"
                        name="ccaTariffEffectiveDate"
                        label="CCA effective start"
                      />
                      <Field as={Input} type="date" name="ccaTariffEndDate" label="CCA effective end" />
                    </div>
                    <Field
                      as={Input}
                      name="ccaVintageYear"
                      label="CCA vintage year"
                      description="Used for generation rate lookup"
                    />
                  </div>
                ) : null}

                <Field
                  component={Select}
                  name="meterAccountType"
                  label="Meter account type"
                  required
                  options={{ BENEFITTING: 'Benefitting', GENERATOR: 'Generator' }}
                />

                <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={values.postBillingTransition}
                    onChange={(e) => void setFieldValue('postBillingTransition', e.target.checked)}
                  />
                  Post-billing transition
                </label>

                {optiStatus === 'PENDING' || optiStatus === 'ENROLLED' ? (
                  <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                    OptiRate is {optiStatus}. Confirming a service-tariff change will update the
                    OptiRate target tariff to match.
                  </p>
                ) : null}

                <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Button type="button" color="slate" disabled={formSubmitting} onClick={onClose}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={formSubmitting || loadingOptions}>
                    Review changes
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        ) : (
          <div className="mt-4 space-y-3">
            {diffs.length === 0 ? (
              <p className="text-sm text-slate-500">No changes.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {diffs.map((d) => (
                  <li
                    key={d.label}
                    className="rounded-md border border-slate-200 px-3 py-2 dark:border-slate-600"
                  >
                    <div className="font-medium text-slate-800 dark:text-slate-100">{d.label}</div>
                    <div className="text-slate-600 dark:text-slate-300">
                      {d.from} → {d.to}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {willSyncOptiRate ? (
              <p className="text-xs text-amber-700 dark:text-amber-300">
                OptiRate target tariff will be set to {compare.tariffSchedule}.
              </p>
            ) : null}
            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                color="slate"
                disabled={isSubmitting}
                onClick={() => setStep('form')}
              >
                Back
              </Button>
              <Button
                type="button"
                disabled={isSubmitting || diffs.length === 0}
                onClick={() => void handleConfirm()}
              >
                {isSubmitting ? 'Saving…' : 'Confirm'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </CustomContentModal>
  )
}

export default ServiceAgreementEditModal
